import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/components/feature/AdminAuthContext';
import { getSupabase } from '@/lib/supabase';
import Logo from '@/components/feature/Logo';

interface AppDetail {
  id: string;
  reference_number: string;
  client_full_name: string;
  client_phone: string;
  client_email: string;
  client_nationality: string;
  passport_number: string;
  country_of_travel: string;
  visa_type: string;
  purpose_of_travel: string;
  intended_travel_date: string;
  collection_address: string;
  application_type: string;
  appointment_date: string;
  appointment_time: string;
  preferred_collection_date: string;
  additional_notes: string;
  current_stage: number;
  final_decision: string;
  submitted_at: string;
}

interface Stage {
  id: string;
  stage_number: number;
  stage_name: string;
  completed: boolean;
}

interface Doc {
  id: string;
  document_type: string;
  file_name: string;
}

interface Note {
  id: string;
  note_type: string;
  content: string;
  created_at: string;
}

interface AppointmentInfo {
  id: string;
  appointment_date: string;
  appointment_time: string;
  status: string;
  completed_at: string | null;
  confirmed_appointment_date: string | null;
  confirmed_appointment_time: string | null;
}

const stageLabels: Record<number, string> = {
  1: 'Application Received', 2: 'Documents Collected', 3: 'Documents Under Review',
  4: 'Documents Verified', 5: 'Appointment Scheduled', 6: 'Submitted to Embassy / Visa Centre',
  7: 'Embassy Processing', 8: 'Additional Information Requested', 9: 'Decision Received',
  10: 'Approved / Declined', 11: 'Passport Ready for Collection / Delivery',
};

export default function ClientApplicationDetail() {
  const { id } = useParams<{ id: string }>();
  const { staffName, logout } = useAdminAuth();
  const navigate = useNavigate();

  const [app, setApp] = useState<AppDetail | null>(null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const [selectedStage, setSelectedStage] = useState(0);
  const [selectedDecision, setSelectedDecision] = useState('pending');
  const [clientNote, setClientNote] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Appointment state
  const [appointment, setAppointment] = useState<AppointmentInfo | null>(null);
  const [apptSaving, setApptSaving] = useState(false);
  const [apptFeedback, setApptFeedback] = useState('');

  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const supabase = getSupabase();
      const [appRes, stagesRes, docsRes, notesRes, apptRes] = await Promise.all([
        supabase.from('applications').select('*').eq('id', id).maybeSingle(),
        supabase.from('application_stages').select('*').eq('application_id', id).order('stage_number'),
        supabase.from('application_documents').select('*').eq('application_id', id),
        supabase.from('admin_notes').select('*').eq('application_id', id).order('created_at', { ascending: false }),
        supabase.from('appointments').select('*').eq('application_id', id).order('created_at', { ascending: false }).maybeSingle(),
      ]);

      if (appRes.error) throw appRes.error;
      if (!appRes.data) { setError('Application not found'); setLoading(false); return; }

      setApp(appRes.data);
      setStages(stagesRes.data || []);
      setDocs(docsRes.data || []);
      setNotes(notesRes.data || []);
      setAppointment(apptRes.data || null);
      setSelectedStage(appRes.data.current_stage);
      setSelectedDecision(appRes.data.final_decision);
      setClientNote(
        notesRes.data?.find((n: Note) => n.note_type === 'client_visible')?.content || appRes.data.additional_notes || ''
      );
      setInternalNote(
        notesRes.data?.find((n: Note) => n.note_type === 'internal')?.content || ''
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  const handleUpdateProgress = async () => {
    if (!id || !app) return;
    setSaving(true);
    try {
      const supabase = getSupabase();

      await supabase.from('applications').update({
        current_stage: selectedStage,
        final_decision: selectedDecision,
      }).eq('id', id);

      for (const stage of stages) {
        const shouldComplete = stage.stage_number <= selectedStage;
        if (stage.completed !== shouldComplete) {
          await supabase.from('application_stages').update({
            completed: shouldComplete,
            completed_at: shouldComplete ? new Date().toISOString() : null,
          }).eq('id', stage.id);
        }
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      fetchData();
    } catch {
      setError('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const showApptFeedback = (msg: string) => {
    setApptFeedback(msg);
    setTimeout(() => setApptFeedback(''), 4000);
  };

  const handleCancelAppointment = async () => {
    if (!appointment || !window.confirm('Cancel this appointment? The time slot will become available again.')) return;
    setApptSaving(true);
    try {
      const supabase = getSupabase();
      const { data, error: fnErr } = await supabase.functions.invoke('manage-appointments?action=cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'cancel', appointment_id: appointment.id }),
      });

      if (fnErr) {
        setError(fnErr instanceof Error ? fnErr.message : 'Edge function error');
        return;
      }
      if (data?.error) { setError(data.error); return; }
      showApptFeedback('Appointment cancelled.');
      fetchData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to cancel');
    } finally {
      setApptSaving(false);
    }
  };

  const handleCompleteAppointment = async () => {
    if (!appointment) return;
    setApptSaving(true);
    try {
      const supabase = getSupabase();
      const { data, error: fnErr } = await supabase.functions.invoke('manage-appointments?action=complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'complete', appointment_id: appointment.id }),
      });

      if (fnErr) {
        setError(fnErr instanceof Error ? fnErr.message : 'Edge function error');
        return;
      }
      if (data?.error) { setError(data.error); return; }
      showApptFeedback('Appointment marked as completed.');
      fetchData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setApptSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!id || !app) return;
    setSaving(true);
    try {
      const supabase = getSupabase();

      const existingClientNote = notes.find((n) => n.note_type === 'client_visible');
      if (existingClientNote) {
        await supabase.from('admin_notes').update({ content: clientNote }).eq('id', existingClientNote.id);
      } else if (clientNote) {
        await supabase.from('admin_notes').insert({ application_id: id, note_type: 'client_visible', content: clientNote });
      }

      const existingInternal = notes.find((n) => n.note_type === 'internal');
      if (existingInternal) {
        await supabase.from('admin_notes').update({ content: internalNote }).eq('id', existingInternal.id);
      } else if (internalNote) {
        await supabase.from('admin_notes').insert({ application_id: id, note_type: 'internal', content: internalNote });
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      fetchData();
    } catch {
      setError('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadgeClass = (decision: string) => {
    if (decision === 'approved') return 'bg-green-100 text-green-700';
    if (decision === 'declined') return 'bg-red-100 text-red-700';
    return 'bg-accent-100 text-accent-700';
  };
  const getStatusLabel = (decision: string) => {
    if (decision === 'approved') return 'Approved';
    if (decision === 'declined') return 'Declined';
    return 'In Progress';
  };

  const getApptStatusLabel = (status: string) => {
    if (status === 'confirmed') return 'Confirmed';
    if (status === 'cancelled') return 'Cancelled';
    if (status === 'completed') return 'Completed';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getApptStatusStyle = (status: string) => {
    if (status === 'confirmed') return 'bg-green-100 text-green-700';
    if (status === 'cancelled') return 'bg-red-100 text-red-700';
    if (status === 'completed') return 'bg-secondary-100 text-foreground-600';
    return 'bg-secondary-100 text-foreground-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
          <p className="text-sm text-foreground-500 mt-3">Loading application...</p>
        </div>
      </div>
    );
  }

  if (error || !app) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center bg-secondary-200 rounded-full mb-4">
            <i className="ri-error-warning-line text-2xl text-foreground-500"></i>
          </div>
          <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-2">{error || 'Not Found'}</h2>
          <Link to="/admin/applications" className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 transition-colors text-sm whitespace-nowrap cursor-pointer">Back to Applications</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <header className="bg-primary-900 sticky top-0 z-40">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-14 md:h-16">
            <div className="flex items-center gap-3">
              <Link to="/admin/applications" className="w-8 h-8 flex items-center justify-center bg-background-50/10 text-background-50 rounded-md hover:bg-background-50/20 transition-colors cursor-pointer">
                <i className="ri-arrow-left-line text-sm"></i>
              </Link>
              <Logo variant="light" className="w-[130px] md:w-[170px]" />
              <div className="hidden sm:block">
                <span className="font-heading font-bold text-sm md:text-base text-background-50 whitespace-nowrap">PVS Admin</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:block text-background-50/80 text-sm">{staffName}</span>
              <button onClick={handleLogout} className="px-3 py-1.5 bg-background-50/10 text-background-50 text-xs font-medium rounded-md border border-background-50/20 hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer">Logout</button>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-8 h-8 flex items-center justify-center text-background-50 cursor-pointer">
                <i className={`text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="py-6 md:py-10">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">{app.reference_number}</h2>
              <p className="text-sm text-foreground-600">{app.client_full_name} — {app.country_of_travel}</p>
            </div>
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold self-start ${getStatusBadgeClass(selectedDecision)}`}>
              {getStatusLabel(selectedDecision)}
            </span>
          </div>

          {saved && (
            <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-md p-3 mb-6 flex items-center gap-2">
              <i className="ri-check-line"></i> Application updated successfully.
            </div>
          )}

          <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
            <div className="flex-1 space-y-6">
              <div className="bg-background-50 border border-secondary-200 rounded-lg p-5 md:p-6">
                <h3 className="font-heading font-semibold text-base text-foreground-900 mb-4">Client Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    ['Full Name', app.client_full_name],
                    ['Phone', app.client_phone],
                    ['Email', app.client_email],
                    ['Nationality', app.client_nationality],
                    ['Passport Number', app.passport_number],
                    ['Submission Date', app.submitted_at?.split('T')[0]],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <p className="text-xs text-foreground-500">{label}</p>
                      <p className="text-sm font-medium text-foreground-900">{value}</p>
                    </div>
                  ))}
                  <div className="sm:col-span-2">
                    <p className="text-xs text-foreground-500">Application Type</p>
                    <p className="text-sm font-medium text-foreground-900">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        app.application_type === 'office_appointment' ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-700'
                      }`}>
                        {app.application_type === 'office_appointment' ? 'Office Appointment' : 'Home Collection'}
                      </span>
                    </p>
                  </div>
                  {app.application_type === 'office_appointment' && (
                    <>
                      <div>
                        <p className="text-xs text-foreground-500">Appointment Date</p>
                        <p className="text-sm font-medium text-foreground-900">{app.appointment_date || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">Appointment Time</p>
                        <p className="text-sm font-medium text-foreground-900">{app.appointment_time || 'N/A'}</p>
                      </div>
                    </>
                  )}
                  {app.application_type === 'home_collection' && (
                    <>
                      <div className="sm:col-span-2">
                        <p className="text-xs text-foreground-500">Collection Address</p>
                        <p className="text-sm font-medium text-foreground-900">{app.collection_address || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">Collection Date</p>
                        <p className="text-sm font-medium text-foreground-900">{app.preferred_collection_date || 'N/A'}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="bg-background-50 border border-secondary-200 rounded-lg p-5 md:p-6">
                <h3 className="font-heading font-semibold text-base text-foreground-900 mb-4">Travel Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    ['Country of Travel', app.country_of_travel],
                    ['Visa Type', app.visa_type],
                    ['Purpose of Travel', app.purpose_of_travel],
                    ['Intended Travel Date', app.intended_travel_date || 'N/A'],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <p className="text-xs text-foreground-500">{label}</p>
                      <p className="text-sm font-medium text-foreground-900">{value}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-background-50 border border-secondary-200 rounded-lg p-5 md:p-6">
                <h3 className="font-heading font-semibold text-base text-foreground-900 mb-4">Uploaded Documents</h3>
                {docs.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {docs.map((doc) => (
                      <div key={doc.id} className="flex items-center gap-3 bg-secondary-50 border border-secondary-200 rounded-md px-4 py-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-md flex-shrink-0">
                          <i className="ri-file-text-line text-primary-600 text-sm"></i>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-foreground-900">{doc.document_type}</p>
                          <p className="text-xs text-foreground-500">{doc.file_name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-foreground-500">No documents uploaded yet.</p>
                )}
              </div>
            </div>

            <div className="lg:w-[400px] flex-shrink-0 space-y-6">
              {/* Appointment Management — only for office_appointment */}
              {app?.application_type === 'office_appointment' && (
                <div className="bg-background-50 border border-accent-200 rounded-lg p-5 md:p-6">
                  <h3 className="font-heading font-semibold text-base text-foreground-900 mb-4 flex items-center gap-2">
                    <i className="ri-calendar-check-line text-accent-600"></i> Office Appointment
                  </h3>

                  {apptFeedback && (
                    <div className="bg-green-50 border border-green-200 text-green-700 text-xs rounded-md p-2.5 mb-4 flex items-center gap-2">
                      <i className="ri-check-line"></i> {apptFeedback}
                    </div>
                  )}

                  {appointment ? (
                    <div className="space-y-3">
                      <div className="bg-secondary-50 border border-secondary-200 rounded-md p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-foreground-500">Status</span>
                          <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${getApptStatusStyle(appointment.status)}`}>
                            {getApptStatusLabel(appointment.status)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <p className="text-xs text-foreground-500">Date</p>
                            <p className="text-sm font-semibold text-foreground-900">{appointment.confirmed_appointment_date || appointment.appointment_date}</p>
                          </div>
                          <div>
                            <p className="text-xs text-foreground-500">Time</p>
                            <p className="text-sm font-semibold text-foreground-900">{appointment.confirmed_appointment_time || appointment.appointment_time}</p>
                          </div>
                        </div>
                      </div>

                      {appointment.status === 'confirmed' && (
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={handleCompleteAppointment}
                            disabled={apptSaving}
                            className="w-full px-4 py-2.5 bg-secondary-100 text-foreground-700 font-medium rounded-md hover:bg-secondary-200 disabled:opacity-60 disabled:cursor-not-allowed transition-colors text-sm whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-check-double-line mr-1.5"></i>Mark as Completed
                          </button>
                          <button
                            onClick={handleCancelAppointment}
                            disabled={apptSaving}
                            className="w-full px-4 py-2.5 bg-red-100 text-red-700 font-medium rounded-md hover:bg-red-200 disabled:opacity-60 disabled:cursor-not-allowed transition-colors text-sm whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-close-line mr-1.5"></i>Cancel Appointment
                          </button>
                        </div>
                      )}

                      {appointment.status === 'cancelled' && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-center">
                          <p className="text-xs text-red-600">This appointment has been cancelled. The time slot is now available for other clients.</p>
                        </div>
                      )}

                      {appointment.status === 'completed' && (
                        <div className="bg-green-50 border border-green-200 rounded-md p-3 text-center">
                          <p className="text-xs text-green-600">This appointment has been completed.</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-foreground-500">No appointment record found for this application.</p>
                  )}
                </div>
              )}

              <div className="bg-background-50 border border-secondary-200 rounded-lg p-5 md:p-6">
                <h3 className="font-heading font-semibold text-base text-foreground-900 mb-4">Update Status</h3>
                <div className="mb-4">
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">Final Decision</label>
                  <select value={selectedDecision} onChange={(e) => setSelectedDecision(e.target.value)} className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors cursor-pointer">
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="declined">Declined</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">Current Stage</label>
                  <select value={selectedStage} onChange={(e) => setSelectedStage(Number(e.target.value))} className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors cursor-pointer">
                    {[1,2,3,4,5,6,7,8,9,10,11].map((n) => (
                      <option key={n} value={n}>Stage {n}: {stageLabels[n]}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-medium text-foreground-700 mb-2">Tick Completed Stages</label>
                  <div className="space-y-1 max-h-[260px] overflow-y-auto">
                    {stages.map((s) => (
                      <label key={s.id} className="flex items-center gap-3 cursor-pointer py-1.5 px-2 rounded hover:bg-secondary-50 transition-colors">
                        <input type="checkbox" checked={s.stage_number <= selectedStage} onChange={() => setSelectedStage(s.stage_number)} className="w-4 h-4 text-accent-600 border-secondary-300 rounded focus:ring-accent-400 cursor-pointer" />
                        <span className={`text-sm ${s.stage_number <= selectedStage ? 'text-foreground-900 font-medium' : 'text-foreground-500'}`}>{s.stage_number}. {s.stage_name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <button onClick={handleUpdateProgress} disabled={saving} className="w-full px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-md hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors text-sm whitespace-nowrap cursor-pointer mb-3">
                  {saving ? 'Updating...' : 'Update Progress'}
                </button>
                <div className="flex gap-3">
                  <button onClick={() => { setSelectedDecision('approved'); setSelectedStage(11); }} className="flex-1 px-4 py-2.5 bg-green-600 text-background-50 font-medium rounded-md hover:bg-green-700 transition-colors text-sm whitespace-nowrap cursor-pointer">Approve</button>
                  <button onClick={() => { setSelectedDecision('declined'); setSelectedStage(10); }} className="flex-1 px-4 py-2.5 bg-red-600 text-background-50 font-medium rounded-md hover:bg-red-700 transition-colors text-sm whitespace-nowrap cursor-pointer">Decline</button>
                </div>
              </div>

              <div className="bg-background-50 border border-secondary-200 rounded-lg p-5 md:p-6">
                <h3 className="font-heading font-semibold text-base text-foreground-900 mb-4">Notes</h3>
                <div className="mb-4">
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">Client-Facing Notes</label>
                  <textarea value={clientNote} onChange={(e) => setClientNote(e.target.value)} maxLength={500} rows={3} className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none" placeholder="Notes visible to client..."></textarea>
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">Internal Staff Notes</label>
                  <textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} maxLength={500} rows={3} className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-amber-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none" placeholder="Internal notes — not visible to client..."></textarea>
                </div>
                <button onClick={handleSaveNotes} disabled={saving} className="w-full px-6 py-2.5 bg-secondary-100 text-foreground-700 font-medium rounded-md hover:bg-secondary-200 transition-colors text-sm whitespace-nowrap cursor-pointer">
                  {saving ? 'Saving...' : 'Save Notes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}